import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

export default function HostingPage() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');

  const plans = [
    {
      name: 'Básico',
      monthlyPrice: 59000,
      annualPrice: 59000,
      popular: false,
      features: [
        '2 GB SSD',
        'Tráfico Ilimitado',
        '5 Cuentas Email',
        '1 Base de Datos MySQL',
        'SSL Gratis',
        'cPanel incluido',
        'Respaldos Diarios',
        'Soporte 24/7',
        'CloudLinux',
        'LiteSpeed Cache',
        'Protección DDoS',
        'Migración Gratis'
      ]
    },
    {
      name: 'Pyme',
      monthlyPrice: 85000,
      annualPrice: 85000,
      popular: true,
      features: [
        '20 GB SSD',
        'Tráfico Ilimitado',
        '25 Cuentas Email',
        '10 Bases de Datos MySQL',
        'SSL Gratis',
        'cPanel incluido',
        'Respaldos Diarios',
        'Soporte Prioritario 24/7',
        'CloudLinux',
        'LiteSpeed Cache',
        'Protección DDoS',
        'Migración Gratis'
      ]
    },
    {
      name: 'Empresarial',
      monthlyPrice: 150000,
      annualPrice: 150000,
      popular: false,
      features: [
        '50 GB SSD NVMe',
        'Tráfico Ilimitado',
        '50 Cuentas Email',
        'Bases de Datos Ilimitadas',
        'SSL Gratis',
        'cPanel incluido',
        'Respaldos Diarios',
        'Soporte Prioritario 24/7',
        'CloudLinux',
        'LiteSpeed Cache',
        'Protección DDoS',
        'Migración Gratis',
        'IP Dedicada'
      ]
    },
    {
      name: 'Avanzado',
      monthlyPrice: 230000,
      annualPrice: 230000,
      popular: false,
      features: [
        '100 GB SSD NVMe',
        'Tráfico Ilimitado',
        'Cuentas Email Ilimitadas',
        'Bases de Datos Ilimitadas',
        'SSL Gratis',
        'cPanel incluido',
        'Respaldos Diarios',
        'Soporte VIP 24/7',
        'CloudLinux',
        'LiteSpeed Cache',
        'Protección DDoS',
        'Migración Gratis',
        'IP Dedicada',
        'CDN Gratis'
      ]
    }
  ];

  const comparisonFeatures = [
    { name: 'Espacio SSD', basic: '2 GB', pro: '20 GB', business: '50 GB', advanced: '100 GB' },
    { name: 'Transferencia', basic: 'Ilimitada', pro: 'Ilimitada', business: 'Ilimitada', advanced: 'Ilimitada' },
    { name: 'Cuentas Email', basic: '5', pro: '25', business: '50', advanced: 'Ilimitadas' },
    { name: 'Bases de Datos', basic: '1', pro: '10', business: 'Ilimitadas', advanced: 'Ilimitadas' },
    { name: 'SSL Gratis', basic: true, pro: true, business: true, advanced: true },
    { name: 'cPanel', basic: true, pro: true, business: true, advanced: true },
    { name: 'Respaldos', basic: 'Diarios', pro: 'Diarios', business: 'Diarios', advanced: 'Diarios' },
    { name: 'CloudLinux', basic: true, pro: true, business: true, advanced: true },
    { name: 'LiteSpeed Cache', basic: true, pro: true, business: true, advanced: true },
    { name: 'IP Dedicada', basic: false, pro: false, business: true, advanced: true },
    { name: 'CDN Gratis', basic: false, pro: false, business: false, advanced: true },
    { name: 'Protección DDoS', basic: true, pro: true, business: true, advanced: true },
    { name: 'Migración Gratis', basic: true, pro: true, business: true, advanced: true }
  ];

  const benefits = [
    {
      icon: 'ri-speed-up-line',
      title: 'Velocidad Extrema',
      description: 'Servidores con discos SSD NVMe y LiteSpeed para máxima velocidad'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Seguridad Avanzada',
      description: 'SSL gratis, firewall, protección DDoS y backups automáticos'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Soporte Experto',
      description: 'Equipo técnico disponible 24/7 en español para ayudarte'
    },
    {
      icon: 'ri-global-line',
      title: 'Servidores en Chile, E.E.U.U. o Europa',
      description: 'Datacenter Tier3 con baja latencia para tu audiencia local'
    },
    {
      icon: 'ri-dashboard-line',
      title: 'cPanel Incluido',
      description: 'Panel de control intuitivo para gestionar tu hosting fácilmente'
    },
    {
      icon: 'ri-arrow-up-down-line',
      title: 'Escalable',
      description: 'Actualiza tu plan en cualquier momento según tus necesidades'
    }
  ];

  const testimonials = [
    {
      name: 'María González',
      company: 'Tienda Online',
      text: 'Excelente servicio, mi sitio carga muy rápido y el soporte técnico siempre responde rápido.',
      rating: 5
    },
    {
      name: 'Carlos Pérez',
      company: 'Agencia Digital',
      text: 'Llevamos 3 años con EhostingChile y nunca hemos tenido problemas. Muy recomendable.',
      rating: 5
    },
    {
      name: 'Andrea Silva',
      company: 'Blog Personal',
      text: 'La migración fue gratuita y sin complicaciones. El equipo técnico es muy profesional.',
      rating: 5
    }
  ];

  return (
    <>
      <Helmet>
        <title>Planes de Hosting Web en Santiago | Servidores Rápidos y Seguros en Chile</title>
        <meta name="description" content="Hosting web profesional en Santiago con servidores de alta velocidad, certificados SSL gratuitos, cPanel incluido y soporte técnico local. Planes desde $59.000 anuales para empresas y profesionales en Chile." />
        <meta name="keywords" content="hosting web santiago, planes hosting chile, servidor web chile, hosting empresarial, cPanel chile, SSL gratuito, hosting profesional santiago, alojamiento web chile" />
        <link rel="canonical" href="https://www.ehostingchile.com/hosting" />
        <meta property="og:title" content="Planes de Hosting Web en Santiago | Servidores Rápidos y Seguros en Chile" />
        <meta property="og:description" content="Hosting web profesional en Santiago con servidores de alta velocidad, certificados SSL gratuitos, cPanel incluido y soporte técnico local." />
        <meta property="og:url" content="https://www.ehostingchile.com/hosting" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Hosting Web Profesional",
            "description": "Planes de hosting web en Chile con servidores de alta velocidad y soporte técnico local",
            "brand": {
              "@type": "Brand",
              "name": "EhostingChile"
            },
            "offers": {
              "@type": "AggregateOffer",
              "lowPrice": "59000",
              "highPrice": "230000",
              "priceCurrency": "CLP",
              "availability": "https://schema.org/InStock"
            }
          })}
        </script>
      </Helmet>
      <div className="bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 text-white py-20 sm:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/20"></div>
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-300 rounded-full blur-3xl"></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-3xl sm:text-5xl font-bold mb-4 sm:mb-6">
                <strong>Hosting Web Profesional</strong> en Chile
              </h1>
              <p className="text-base sm:text-xl text-blue-100 mb-6 sm:mb-8">
                Alojamiento web rápido, seguro y confiable con <strong>servidores en Chile E.E.U.U. y/o Europa</strong>. 
                Perfecto para sitios web, tiendas online y aplicaciones empresariales con <strong>certificados SSL gratuitos</strong> y <strong>cPanel incluido</strong>.
              </p>
              <div className="flex flex-wrap gap-2 sm:gap-3 justify-center">
                {['99.9% Uptime', 'SSL Gratis', 'Soporte 24/7', 'CloudLinux', 'LiteSpeed Cache', 'Protección DDoS', 'Migración Gratis'].map((item) => (
                  <div key={item} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full">
                    <i className="ri-check-line text-green-300 text-sm"></i>
                    <span className="text-xs sm:text-sm whitespace-nowrap">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Plans Section */}
        <section className="py-12 sm:py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
                Planes de Hosting con Tarifa Anual
              </h2>
              <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
                Todos los precios son anuales e incluyen IVA. Elige el plan que mejor se adapte a tus necesidades.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
              {plans.map((plan, index) => (
                <div
                  key={index}
                  className={`relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 ${
                    plan.popular ? 'ring-2 ring-blue-600 sm:transform sm:scale-105' : ''
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-5 sm:px-6 py-1 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap">
                      Más Popular
                    </div>
                  )}
                  
                  <div className="p-5 sm:p-6">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    {plan.name === 'Pyme' && <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">Perfecto para negocios</p>}
                    {plan.name === 'Empresarial' && <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">Para proyectos grandes</p>}
                    {plan.name === 'Avanzado' && <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">Máximo rendimiento</p>}
                    <div className="mb-5 sm:mb-6">
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl sm:text-4xl font-bold text-gray-900">
                          ${plan.annualPrice.toLocaleString('es-CL')}
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-600 mt-1">+ IVA Anual</p>
                    </div>

                    <ul className="space-y-2 sm:space-y-3 mb-5 sm:mb-6">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm">
                          <i className="ri-check-line text-green-600 mt-0.5 flex-shrink-0"></i>
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Link
                      to="/contratar"
                      className={`block w-full text-center py-2.5 sm:py-3 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base ${
                        plan.popular
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                      }`}
                    >
                      Contratar Plan
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-12 sm:py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
                Comparación Detallada de Planes
              </h2>
              <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
                Compara todas las características de nuestros planes de hosting
              </p>
            </div>

            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <div className="min-w-[600px] px-4 sm:px-0">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left py-3 sm:py-4 px-4 sm:px-6 font-semibold text-gray-900 border-b-2 border-gray-200 text-sm sm:text-base">Características</th>
                      <th className="text-center py-3 sm:py-4 px-3 sm:px-6 font-semibold text-gray-900 border-b-2 border-gray-200 text-sm sm:text-base">Básico</th>
                      <th className="text-center py-3 sm:py-4 px-3 sm:px-6 font-semibold text-gray-900 border-b-2 border-gray-200 bg-blue-50 text-sm sm:text-base">Pyme</th>
                      <th className="text-center py-3 sm:py-4 px-3 sm:px-6 font-semibold text-gray-900 border-b-2 border-gray-200 text-sm sm:text-base">Empresarial</th>
                      <th className="text-center py-3 sm:py-4 px-3 sm:px-6 font-semibold text-gray-900 border-b-2 border-gray-200 text-sm sm:text-base">Avanzado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonFeatures.map((feature, index) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 sm:py-4 px-4 sm:px-6 font-medium text-gray-900 text-xs sm:text-sm">{feature.name}</td>
                        {[feature.basic, feature.pro, feature.business, feature.advanced].map((val, ci) => (
                          <td key={ci} className={`py-3 sm:py-4 px-3 sm:px-6 text-center text-gray-700 text-xs sm:text-sm ${ci === 1 ? 'bg-blue-50' : ''}`}>
                            {typeof val === 'boolean' ? (
                              val ? <i className="ri-check-line text-green-600 text-lg sm:text-xl"></i>
                                 : <i className="ri-close-line text-gray-300 text-lg sm:text-xl"></i>
                            ) : val}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-12 sm:py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
                Todo lo que Necesitas Incluido
              </h2>
              <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
                Todos nuestros planes de hosting incluyen características premium sin costo adicional
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-white rounded-xl p-5 sm:p-6 shadow-md hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                    <i className={`${benefit.icon} text-xl sm:text-2xl text-blue-600`}></i>
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-sm text-gray-600">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-12 sm:py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
                Lo que Dicen Nuestros Clientes
              </h2>
              <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
                Miles de clientes confían en nosotros para su hosting web
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">&ldquo;{testimonial.text}&rdquo;</p>
                  <div className="border-t border-gray-200 pt-4">
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.company}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Highlight Section */}
        <section className="py-12 sm:py-16 bg-gradient-to-br from-blue-50 to-cyan-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
                Características Destacadas
              </h2>
              <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
                Tecnología de punta para garantizar el mejor rendimiento
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-server-line text-2xl text-blue-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Infraestructura Premium</h3>
                    <p className="text-sm text-gray-600">Servidores con procesadores Intel Xeon de última generación, discos SSD NVMe y conexión de alta velocidad para garantizar el mejor rendimiento.</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-2xl text-green-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Seguridad Multicapa</h3>
                    <p className="text-sm text-gray-600">Protección DDoS, firewall avanzado, detección de malware, certificados SSL gratuitos y respaldos automáticos diarios para mantener tus datos seguros.</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-flashlight-line text-2xl text-purple-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">LiteSpeed Web Server</h3>
                    <p className="text-sm text-gray-600">Servidor web hasta 40 veces más rápido que Apache. Incluye LiteSpeed Cache para acelerar WordPress y otras aplicaciones web.</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-md">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-customer-service-2-line text-2xl text-orange-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Soporte Técnico Experto</h3>
                    <p className="text-sm text-gray-600">Equipo técnico certificado disponible 24/7 en español. Respuesta rápida por ticket, teléfono y WhatsApp para resolver cualquier problema.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 sm:py-16 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">¿Listo para Comenzar?</h2>
            <p className="text-base sm:text-xl text-blue-100 mb-6 sm:mb-8">
              Elige tu plan de hosting y comienza a construir tu presencia online hoy mismo
            </p>
            <div className="flex flex-wrap gap-3 sm:gap-4 justify-center">
              <Link to="/contratar" className="bg-white text-blue-600 px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors whitespace-nowrap cursor-pointer text-sm sm:text-base">
                Contratar Ahora
              </Link>
              <Link to="/soporte" className="bg-blue-500 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg font-semibold hover:bg-blue-400 transition-colors whitespace-nowrap cursor-pointer text-sm sm:text-base">
                Hablar con Soporte
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}